import type { DemoScript } from '../../../types/demo';

const AUDIO_BASE = 'https://bigsplice-demo-audio.s3.amazonaws.com/api-pipeline';
const AUDIO_VERSION = 'v5';

/** Hardcoded demo file representing a sample product catalog */
const DEMO_FILE = {
  fileName: 'product_catalog.csv',
  fileSize: 1850000,
  uploadDate: '2026-02-23T10:00:00Z',
  s3Key: 'users/demo/product_catalog.csv',
  contentType: 'text/csv',
};

/** Mock schema for product catalog data */
const DEMO_SCHEMA = [
  { name: 'product_id', data_type: 'INT64' },
  { name: 'name', data_type: 'BYTE_ARRAY' },
  { name: 'description', data_type: 'BYTE_ARRAY' },
  { name: 'price', data_type: 'DOUBLE' },
  { name: 'category', data_type: 'BYTE_ARRAY' },
  { name: 'sku', data_type: 'BYTE_ARRAY' },
  { name: 'stock_level', data_type: 'INT64' },
  { name: 'last_updated', data_type: 'BYTE_ARRAY' },
];

/** Mock rows of product catalog data */
const DEMO_ROWS = [
  { product_id: 3001, name: 'ProMax Wireless Headphones', description: 'Noise-cancelling over-ear headphones with 40hr battery life and adaptive EQ.', price: 249.99, category: 'Audio', sku: 'AUD-WH-3001', stock_level: 342, last_updated: '2026-02-22' },
  { product_id: 3002, name: 'UltraCharge 65W GaN Adapter', description: 'Compact 65W USB-C PD charger with dual ports and foldable prongs.', price: 39.99, category: 'Accessories', sku: 'ACC-CH-3002', stock_level: 1205, last_updated: '2026-02-21' },
  { product_id: 3003, name: 'CloudSync Smart Speaker', description: 'Voice-controlled smart speaker with multi-room audio and Matter support.', price: 129.00, category: 'Audio', sku: 'AUD-SP-3003', stock_level: 87, last_updated: '2026-02-20' },
  { product_id: 3004, name: 'ErgoFlex Laptop Stand', description: 'Adjustable aluminum laptop stand with integrated USB-C hub and cooling vents.', price: 79.95, category: 'Accessories', sku: 'ACC-LS-3004', stock_level: 563, last_updated: '2026-02-20' },
  { product_id: 3005, name: 'NovaPad 12.9" Tablet', description: 'OLED display tablet with stylus support, 256GB storage, and 5G connectivity.', price: 899.00, category: 'Tablets', sku: 'TAB-NP-3005', stock_level: 24, last_updated: '2026-02-19' },
  { product_id: 3006, name: 'Wireless Earbuds SE', description: 'Lightweight wireless earbuds with active noise cancellation and 8hr playback.', price: 89.99, category: 'Audio', sku: 'AUD-EB-3006', stock_level: 718, last_updated: '2026-02-19' },
];

/** Mock search results for "wireless headphones" query */
const DEMO_SEARCH_RESULTS = [
  { product_id: 3001, name: 'ProMax Wireless Headphones', description: 'Noise-cancelling over-ear headphones with 40hr battery life...', price: 249.99, category: 'Audio' },
  { product_id: 3006, name: 'Wireless Earbuds SE', description: 'Lightweight wireless earbuds with active noise cancellation...', price: 89.99, category: 'Audio' },
  { product_id: 3009, name: 'StudioPro Wireless Headphones', description: 'Reference-grade wireless headphones for music production...', price: 349.00, category: 'Audio' },
];

/**
 * API & MCP Pipeline demo script with LIVE DATA injection.
 * Uses hardcoded product_catalog.csv with mock product data
 * flowing through all 5 nodes of the api-pipeline flow template.
 */
export const apiPipelineScript: DemoScript = {
  id: 'api-pipeline',
  templateId: 'api-pipeline',
  name: 'API & MCP Pipeline Demo',
  description: 'Learn how to upload data, browse it, search it, and expose it via REST API for Claude, GPT, and other tools.',
  steps: [
    // === INTRO (no target) ===
    {
      id: 'intro',
      type: 'narrate',
      delayBefore: 300,
      subtitle: 'Let me walk you through the API pipeline \u2014 we\u2019ll turn a product catalog into a live REST API that Claude or GPT can query directly.',
      audio: { src: `${AUDIO_BASE}/intro.mp3?${AUDIO_VERSION}`, duration: 8500 },
    },

    // === STEP 1: Upload Data ===
    {
      id: 'upload-highlight',
      type: 'highlight',
      target: { nodeId: 'upload' },
      delayBefore: 200,
      subtitle: 'Start here. Upload your data \u2014 CSV, Excel, JSON, or PDF. We\u2019ll parse it automatically.',
      audio: { src: `${AUDIO_BASE}/upload-highlight.mp3?${AUDIO_VERSION}`, duration: 7500 },
      cursorPath: [
        { x: -80, y: -40, duration: 600 },
        { x: 0, y: 0, duration: 500 },
      ],
    },
    {
      id: 'upload-sample-note',
      type: 'narrate',
      target: { nodeId: 'upload' },
      subtitle: 'The data you\u2019ll see in this demo is simulated so I can walk you through the full pipeline without delay. Drop in your own file when you\u2019re ready and it\u2019ll run for real.',
      audio: { src: `${AUDIO_BASE}/upload-sample-note.mp3?${AUDIO_VERSION}`, duration: 9000 },
    },
    {
      id: 'upload-decision',
      type: 'decision',
      target: { nodeId: 'upload' },
      subtitle: 'Want me to use our sample product catalog, or would you like to upload your own data?',
      audio: { src: `${AUDIO_BASE}/upload-decision.mp3?${AUDIO_VERSION}`, duration: 4000 },
      decision: {
        prompt: 'Want me to use our sample dataset, or would you like to upload your own?',
        options: [
          { label: 'Use sample dataset', icon: 'database', nextStepId: 'upload-inject-uploading' },
          { label: 'I\u2019ll upload mine', icon: 'upload', nextStepId: 'upload-interact' },
        ],
        timeoutMs: 6000,
        defaultOptionIndex: 0,
      },
    },
    {
      id: 'upload-interact',
      type: 'interact',
      target: { nodeId: 'upload' },
      subtitle: 'Go ahead and drag a file onto the upload area. I\u2019ll wait for you.',
      audio: { src: `${AUDIO_BASE}/upload-interact.mp3?${AUDIO_VERSION}`, duration: 4000 },
      autoAdvanceAfter: 4000,
    },

    // === INJECT: Simulate file upload progress ===
    {
      id: 'upload-inject-uploading',
      type: 'inject',
      target: { nodeId: 'upload' },
      subtitle: 'Uploading file\u2026',
      inject: {
        nodeId: 'upload',
        data: { demoState: { status: 'uploading', fileName: 'product_catalog.csv', progress: 35 } },
      },
      autoAdvanceAfter: 250,
    },
    {
      id: 'upload-inject-progress',
      type: 'inject',
      target: { nodeId: 'upload' },
      inject: {
        nodeId: 'upload',
        data: { demoState: { status: 'uploading', fileName: 'product_catalog.csv', progress: 72 } },
      },
      autoAdvanceAfter: 200,
    },
    {
      id: 'upload-inject-almost',
      type: 'inject',
      target: { nodeId: 'upload' },
      inject: {
        nodeId: 'upload',
        data: { demoState: { status: 'uploading', fileName: 'product_catalog.csv', progress: 95 } },
      },
      autoAdvanceAfter: 150,
    },
    {
      id: 'upload-inject-done',
      type: 'inject',
      target: { nodeId: 'upload' },
      subtitle: 'Upload complete. Your product catalog is ready.',
      audio: { src: `${AUDIO_BASE}/upload-inject.mp3?${AUDIO_VERSION}`, duration: 3000 },
      inject: {
        nodeId: 'upload',
        data: { demoState: { status: 'done', fileName: 'product_catalog.csv' } },
      },
    },

    // === STEP 2: View Data ===
    {
      id: 'dataset-highlight',
      type: 'highlight',
      target: { nodeId: 'sheets' },
      delayBefore: 150,
      subtitle: 'Now let\u2019s view the data we just uploaded.',
      audio: { src: `${AUDIO_BASE}/dataset-highlight.mp3?${AUDIO_VERSION}`, duration: 2000 },
      cursorPath: [
        { x: -40, y: -30, duration: 400 },
        { x: 0, y: 0, duration: 400 },
      ],
    },
    // Pre-inject mock data into downstream nodes BEFORE file selection triggers them
    {
      id: 'sheets-pre-inject',
      type: 'inject',
      inject: {
        nodeId: 'sheets',
        data: {
          demoState: {
            schema: DEMO_SCHEMA,
            rows: DEMO_ROWS,
            rowCount: 12450,
          },
        },
      },
      autoAdvanceAfter: 100,
    },
    {
      id: 'search-pre-inject',
      type: 'inject',
      inject: {
        nodeId: 'search',
        data: {
          demoState: {},
        },
      },
      autoAdvanceAfter: 100,
    },
    // Now inject the file — downstream nodes already have demoState and won't call APIs
    {
      id: 'dataset-inject',
      type: 'inject',
      target: { nodeId: 'sheets' },
      subtitle: 'Loading data\u2026 Watch how the data flows through the pipeline.',
      audio: { src: `${AUDIO_BASE}/dataset-selected.mp3?${AUDIO_VERSION}`, duration: 5000 },
      inject: {
        nodeId: 'sheets',
        data: { selectedFile: DEMO_FILE },
      },
    },

    // === STEP 3: Browse Data (mock data already loaded via demoState) ===
    {
      id: 'sheets-highlight',
      type: 'highlight',
      target: { nodeId: 'sheets' },
      delayBefore: 400,
      subtitle: 'Here\u2019s your product catalog in spreadsheet form. Every row is a product \u2014 names, descriptions, prices, stock levels, all browsable.',
      audio: { src: `${AUDIO_BASE}/sheets-highlight.mp3?${AUDIO_VERSION}`, duration: 9500 },
      cursorPath: [
        { x: -100, y: -60, duration: 500 },
        { x: 0, y: 0, duration: 500 },
        { x: 50, y: 30, duration: 400 },
      ],
    },
    {
      id: 'sheets-decision',
      type: 'decision',
      target: { nodeId: 'sheets' },
      subtitle: '',
      decision: {
        prompt: 'Want to explore the data?',
        options: [
          { label: 'Explore the data', icon: 'search', nextStepId: 'sheets-interact' },
          { label: 'Show me search', icon: 'arrow-right', nextStepId: 'search-highlight' },
          { label: 'Skip to API', icon: 'skip-forward', nextStepId: 'api-highlight' },
        ],
        timeoutMs: 6000,
        defaultOptionIndex: 1,
      },
    },
    {
      id: 'sheets-interact',
      type: 'interact',
      target: { nodeId: 'sheets' },
      subtitle: 'Take a moment to browse through the catalog. Click any column header to sort, or scroll to see more products.',
      audio: { src: `${AUDIO_BASE}/sheets-interact.mp3?${AUDIO_VERSION}`, duration: 6500 },
      autoAdvanceAfter: 4000,
    },

    // === STEP 4: Full-Text Search ===
    {
      id: 'search-highlight',
      type: 'highlight',
      target: { nodeId: 'search' },
      subtitle: 'Full-text search across your entire catalog. Find products by name, description, SKU, or any keyword \u2014 instantly.',
      audio: { src: `${AUDIO_BASE}/search-highlight.mp3?${AUDIO_VERSION}`, duration: 10500 },
      cursorPath: [
        { x: -60, y: -40, duration: 400 },
        { x: 0, y: 0, duration: 500 },
      ],
    },
    {
      id: 'search-decision',
      type: 'decision',
      target: { nodeId: 'search' },
      subtitle: '',
      decision: {
        prompt: 'Want to try a search?',
        options: [
          { label: 'Watch demo search', icon: 'play', nextStepId: 'search-demo' },
          { label: 'Try it myself', icon: 'keyboard', nextStepId: 'search-interact' },
        ],
        timeoutMs: 5000,
        defaultOptionIndex: 0,
      },
    },
    {
      id: 'search-demo',
      type: 'interact',
      target: { nodeId: 'search' },
      subtitle: 'Let me search for something \u2014 watch this.',
      audio: { src: `${AUDIO_BASE}/search-typing.mp3?${AUDIO_VERSION}`, duration: 5500 },
      interaction: { action: 'type', delay: 100, value: 'wireless headphones' },
      autoAdvanceAfter: 1200,
    },
    // Inject mock search results after typing
    {
      id: 'search-inject-results',
      type: 'inject',
      target: { nodeId: 'search' },
      inject: {
        nodeId: 'search',
        data: {
          demoState: {
            results: DEMO_SEARCH_RESULTS,
            resultCount: 3,
          },
        },
      },
      autoAdvanceAfter: 1200,
    },
    {
      id: 'search-interact',
      type: 'interact',
      target: { nodeId: 'search' },
      subtitle: 'Type any search term into the search box. Try "laptop stand" or "noise cancelling".',
      audio: { src: `${AUDIO_BASE}/search-interact.mp3?${AUDIO_VERSION}`, duration: 6000 },
      autoAdvanceAfter: 4000,
    },

    // === STEP 5: API Endpoint ===
    {
      id: 'api-highlight',
      type: 'highlight',
      target: { nodeId: 'api-endpoint' },
      subtitle: 'This is the key \u2014 your data is now a live REST API. Generate an API key, and Claude, GPT, or any MCP-compatible tool can query your catalog directly. No custom backend needed.',
      audio: { src: `${AUDIO_BASE}/api-highlight.mp3?${AUDIO_VERSION}`, duration: 16500 },
      cursorPath: [
        { x: -60, y: -30, duration: 500 },
        { x: 0, y: 0, duration: 400 },
      ],
    },

    // === ENDING ===
    {
      id: 'ending',
      type: 'narrate',
      subtitle: 'That\u2019s the full pipeline \u2014 from raw data to a searchable, API-accessible product catalog. Claude and GPT can now query your data in real time.',
      audio: { src: `${AUDIO_BASE}/ending.mp3?${AUDIO_VERSION}`, duration: 9000 },
    },
    {
      id: 'ending-decision',
      type: 'decision',
      subtitle: '',
      decision: {
        prompt: 'What would you like to do next?',
        options: [
          { label: 'Start my own flow', icon: 'rocket', nextStepId: '__dismiss' },
          { label: 'Replay demo', icon: 'repeat', nextStepId: '__replay' },
          { label: 'Ask a question', icon: 'help-circle', nextStepId: '__ask' },
        ],
        timeoutMs: 0,
        defaultOptionIndex: -1,
      },
    },
  ],
};
